//codex-- Page-scoped toolbar layout; retain this project's handlers and data.
import PageToolbar, { ToolbarField, ToolbarFooterAction } from './PageToolbar';
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate, useLocation } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';

import SideNav from '../components/SideNav';
import HeadingCp from '../components/HeadingCp';
import LoadingSpinner from '../components/LoadingSpinner';

import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from 'recharts';

const AttendanceAnalyticsPage = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const api = `${apiUrl}/tech_crud.php`;
    const navigate = useNavigate();
    const location = useLocation();

  const EXPECTED_START_TIME = '08:00:00';

  const [loading, setLoading] = useState(false);
  const [employeesLoading, setEmployeesLoading] = useState(false);

  const [employees, setEmployees] = useState([]);
  const [rawData, setRawData] = useState([]);

  const [filterEmployee, setFilterEmployee] = useState('');
  // const [graphRange, setGraphRange] = useState('6m'); // 1m | 6m | 12m
  const [graphMetric, setGraphMetric] = useState('avgLateMinutes'); // avgLateMinutes | lateCount | onTimeRate | totalLateMinutes

  const [analyticsMode, setAnalyticsMode] = useState('trend'); // trend | leaderboard

  const getRangeDates = (year) => {
    return {
      dateFrom: `${year}-01-01`,
      dateTo: `${year}-12-31`,
    };
  };

  const [allTableRows, setAllTableRows] = useState([]);  

  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const yearOptions = useMemo(() => {
  const years = [];
  for (let y = currentYear; y >= currentYear - 5; y--) {
    years.push(y);
  }
    return years;
  }, [currentYear]);

  const timeStringToMinutes = (timeString) => {
    if (!timeString) return null;
    const parts = timeString.split(':').map(Number);
    const hours = parts[0] || 0;
    const minutes = parts[1] || 0;
    return (hours * 60) + minutes;
  };

  const unixToDate = (unixSeconds) => {
    if (!unixSeconds) return null;
    return new Date(Number(unixSeconds) * 1000);
  };

  const formatMonthKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  const formatMonthLabel = (monthKey) => {
    const [year, month] = monthKey.split('-');
    const d = new Date(Number(year), Number(month) - 1, 1);
    return d.toLocaleString('en-US', { month: 'short', year: 'numeric' });
  };

  const getLateMinutes = (actualUnixTime, expectedTime = EXPECTED_START_TIME) => {
    const actualDate = unixToDate(actualUnixTime);
    if (!actualDate) return 0;

    const actualMinutes = actualDate.getHours() * 60 + actualDate.getMinutes();
    const expectedMinutes = timeStringToMinutes(expectedTime);

    if (expectedMinutes === null) return 0;

    return Math.max(0, actualMinutes - expectedMinutes);
  };

  const fetchEmployees = async () => {
    setEmployeesLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/service_crud.php?employee_list=true`, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
      });

      const employeeList = Array.isArray(response.data) ? response.data : [];
      setEmployees(employeeList);
    } catch (error) {
      console.error('Error fetching employees:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.', { position: 'top-center' });
        navigate('/login');
      } else if (error.response?.status === 403) {
        toast.error("Oops!! you don't have access to this area. Speak to the CFO.", {
          position: 'top-center',
        });
      } else {
        toast.error('Error loading employees.', { position: 'top-center' });
      }
    } finally {
      setEmployeesLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    const { dateFrom, dateTo } = getRangeDates(selectedYear);

    setLoading(true);
    try {
      const bodyData = {
        status: 'getFilter',
        date_from: dateFrom,
        date_to: dateTo,
        ...(filterEmployee ? { employee_id: filterEmployee } : {}),
      };

      const response = await axios.post(api, bodyData, {
        headers: { 'Content-Type': 'application/json' },
      });

      const data = response.data;

      if (Array.isArray(data)) {
        setRawData(data);
        setAllTableRows([]);
        if (data.length === 0) {
          toast.info('No attendance records found for the selected range.', {
            position: 'top-center',
          });
        }
        return;
      }

      if (data && data.mode === 'all_table' && Array.isArray(data.rows)) {
        setAllTableRows(data.rows);
        setRawData([]);
        if (data.rows.length === 0) {
          toast.info('No attendance records found for the selected range.', {
            position: 'top-center',
          });
        }
        return;
      }

      setRawData([]);
      setAllTableRows([]);
      toast.error('Unexpected analytics data format.', { position: 'top-center' });
    } catch (error) {
      console.error('Error loading analytics:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.', { position: 'top-center' });
        navigate('/login');
      } else if (error.response?.status === 403) {
        toast.error("Oops!! you don't have access to this area. Speak to the CFO.", {
          position: 'top-center',
        });
      } else {
        toast.error('Error loading attendance analytics.', { position: 'top-center' });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, filterEmployee, analyticsMode]);

  useEffect(() => {
  if (analyticsMode === 'leaderboard') {
    setFilterEmployee('');
  }
}, [analyticsMode]);

  const flattenedDays = useMemo(() => {
    const rows = [];

    rawData.forEach((emp) => {
      const employeeName = emp.employee || '';
      const days = emp.days || {};

      Object.keys(days).forEach((dateKey) => {
        const dayData = days[dateKey];
        const amTime = dayData?.am?.time || null;
        const pmTime = dayData?.pm?.time || null;

        if (!amTime) return;

        const amDate = unixToDate(amTime);
        if (!amDate) return;

        const lateMinutes = getLateMinutes(amTime);

        rows.push({
          employee: employeeName,
          date: dateKey,
          monthKey: formatMonthKey(amDate),
          monthLabel: formatMonthLabel(formatMonthKey(amDate)),
          amTimeUnix: amTime,
          pmTimeUnix: pmTime,
          lateMinutes,
          wasLate: lateMinutes > 0,
          wasOnTime: lateMinutes === 0,
        });
      });
    });

    return rows.sort((a, b) => a.date.localeCompare(b.date));
  }, [rawData]);

  const getLateMinutesFromFlatRow = (row) => {
    const amUnixTime = row.am_time || row.time || row.check_in || null;
    if (!amUnixTime) return 0;
    return getLateMinutes(amUnixTime);
  };

  const leaderboardData = useMemo(() => {
    const grouped = {};

    // 1) If we have flat all-company rows, use those
    if (analyticsMode === 'leaderboard' && allTableRows.length > 0) {
      allTableRows.forEach((row) => {
        const employeeName =
          row.employee ||
          row.employee_name ||
          row.tech_name ||
          'Unknown';

        const lateMinutes = getLateMinutesFromFlatRow(row);

        if (!grouped[employeeName]) {
          grouped[employeeName] = {
            employee: employeeName,
            totalDays: 0,
            lateCount: 0,
            onTimeCount: 0,
            totalLateMinutes: 0,
            maxLateMinutes: 0,
          };
        }

        grouped[employeeName].totalDays += 1;
        grouped[employeeName].totalLateMinutes += lateMinutes;

        if (lateMinutes > grouped[employeeName].maxLateMinutes) {
          grouped[employeeName].maxLateMinutes = lateMinutes;
        }

        if (lateMinutes > 0) {
          grouped[employeeName].lateCount += 1;
        } else {
          grouped[employeeName].onTimeCount += 1;
        }
      });
    } else {
      // 2) Existing per-employee days structure
      flattenedDays.forEach((row) => {
        const employeeName = row.employee || 'Unknown';

        if (!grouped[employeeName]) {
          grouped[employeeName] = {
            employee: employeeName,
            totalDays: 0,
            lateCount: 0,
            onTimeCount: 0,
            totalLateMinutes: 0,
            maxLateMinutes: 0,
          };
        }

        grouped[employeeName].totalDays += 1;
        grouped[employeeName].totalLateMinutes += row.lateMinutes;

        if (row.lateMinutes > grouped[employeeName].maxLateMinutes) {
          grouped[employeeName].maxLateMinutes = row.lateMinutes;
        }

        if (row.wasLate) {
          grouped[employeeName].lateCount += 1;
        } else {
          grouped[employeeName].onTimeCount += 1;
        }
      });
    }

    return Object.values(grouped)
      .map((item) => ({
        ...item,
        avgLateMinutes: item.totalDays
          ? Number((item.totalLateMinutes / item.totalDays).toFixed(1))
          : 0,
        onTimeRate: item.totalDays
          ? Number(((item.onTimeCount / item.totalDays) * 100).toFixed(1))
          : 0,
        lateRate: item.totalDays
          ? Number(((item.lateCount / item.totalDays) * 100).toFixed(1))
          : 0,
      }))
      .sort((a, b) => {
        if (b.lateCount !== a.lateCount) return b.lateCount - a.lateCount;
        if (b.totalLateMinutes !== a.totalLateMinutes) {
          return b.totalLateMinutes - a.totalLateMinutes;
        }
        return a.employee.localeCompare(b.employee);
      });
  }, [analyticsMode, allTableRows, flattenedDays]);  

  const renderLeaderboardChart = () => {
  const chartRows = leaderboardData.slice(0, 10);

  return (
    <ResponsiveContainer width="100%" height={420}>
      <BarChart data={chartRows} layout="vertical" margin={{ top: 10, right: 20, left: 40, bottom: 10 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis type="number" />
        <YAxis dataKey="employee" type="category" width={140} />
        <Tooltip />
        <Legend />
        <Bar dataKey="lateCount" name="Late Count" />
      </BarChart>
    </ResponsiveContainer>
  );
};

const leaderboardSummary = useMemo(() => {
  if (!leaderboardData.length) {
    return {
      worstEmployee: null,
      highestAvgLateEmployee: null,
      totalEmployees: 0,
    };
  }

  const worstEmployee = leaderboardData[0];

  const highestAvgLateEmployee = [...leaderboardData].sort(
    (a, b) => b.avgLateMinutes - a.avgLateMinutes
  )[0];

  return {
    worstEmployee,
    highestAvgLateEmployee,
    totalEmployees: leaderboardData.length,
  };
}, [leaderboardData]);

  const graphData = useMemo(() => {
    const grouped = {};

    flattenedDays.forEach((row) => {
      if (!grouped[row.monthKey]) {
        grouped[row.monthKey] = {
          month: row.monthKey,
          label: row.monthLabel,
          totalDays: 0,
          lateCount: 0,
          onTimeCount: 0,
          totalLateMinutes: 0,
        };
      }

      grouped[row.monthKey].totalDays += 1;
      grouped[row.monthKey].totalLateMinutes += row.lateMinutes;

      if (row.wasLate) {
        grouped[row.monthKey].lateCount += 1;
      } else {
        grouped[row.monthKey].onTimeCount += 1;
      }
    });

    return Object.values(grouped)
      .sort((a, b) => a.month.localeCompare(b.month))
      .map((item) => ({
        month: item.month,
        label: item.label,
        totalDays: item.totalDays,
        lateCount: item.lateCount,
        onTimeCount: item.onTimeCount,
        totalLateMinutes: item.totalLateMinutes,
        avgLateMinutes: item.totalDays
          ? Number((item.totalLateMinutes / item.totalDays).toFixed(1))
          : 0,
        onTimeRate: item.totalDays
          ? Number(((item.onTimeCount / item.totalDays) * 100).toFixed(1))
          : 0,
      }));
  }, [flattenedDays]);

  const summary = useMemo(() => {
    const totalDays = flattenedDays.length;
    const lateCount = flattenedDays.filter((row) => row.wasLate).length;
    const onTimeCount = flattenedDays.filter((row) => row.wasOnTime).length;
    const totalLateMinutes = flattenedDays.reduce((sum, row) => sum + row.lateMinutes, 0);

    const avgLateMinutes = totalDays
      ? Number((totalLateMinutes / totalDays).toFixed(1))
      : 0;

    const onTimeRate = totalDays
      ? Number(((onTimeCount / totalDays) * 100).toFixed(1))
      : 0;

    const worstMonth = graphData.length
      ? [...graphData].sort((a, b) => b.avgLateMinutes - a.avgLateMinutes)[0]
      : null;

    return {
      totalDays,
      lateCount,
      onTimeCount,
      totalLateMinutes,
      avgLateMinutes,
      onTimeRate,
      worstMonth,
    };
  }, [flattenedDays, graphData]);

const selectedEmployeeLabel = useMemo(() => {
  if (analyticsMode === 'leaderboard') return 'All Technicians';
  if (!filterEmployee) return 'All Employees';

  const found = employees.find((emp) => String(emp.id) === String(filterEmployee));
  return found ? `${found.first_name} ${found.last_name}` : 'Selected Employee';
}, [analyticsMode, employees, filterEmployee]);

  const renderChart = () => {
    if (graphMetric === 'lateCount') {
      return (
        <ResponsiveContainer width="100%" height={360}>
          <BarChart data={graphData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="lateCount" name="Late Count" />
          </BarChart>
        </ResponsiveContainer>
      );
    }

    if (graphMetric === 'totalLateMinutes') {
      return (
        <ResponsiveContainer width="100%" height={360}>
          <BarChart data={graphData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="totalLateMinutes" name="Total Late Minutes" />
          </BarChart>
        </ResponsiveContainer>
      );
    }

    if (graphMetric === 'onTimeRate') {
      return (
        <ResponsiveContainer width="100%" height={360}>
          <LineChart data={graphData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" />
            <YAxis domain={[0, 100]} />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="onTimeRate" name="On-Time %" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={360}>
        <LineChart data={graphData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="label" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Line type="monotone" dataKey="avgLateMinutes" name="Avg Late Minutes" strokeWidth={2} />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  return (
    <div className="max-w-[1220px] mx-auto grid grid-cols-1 md:grid-cols-[200px_minmax(900px,_1fr)] gap-x-4">
      <div className="hidden md:block">
        <div className="pt-6 pr-3">
          <SideNav />
        </div>
      </div>

      <div className="p-4">
        <div className="px-4">
          <div className="sm:flex sm:items-center justify-between">
            <HeadingCp label="Attendance Analytics" />
          </div>

        <>{/* //codex-- Match the page toolbar reference while retaining each existing control and form owner. */}<PageToolbar title="Attendance Analytics  tools" actions={<><div className="flex items-center justify-center mb-6">
          <div className="flex bg-gray-200 p-1 rounded-lg">            
            <button
              onClick={() => navigate('/vcheckinout')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition ${
                location.pathname === '/vcheckinout'
                  ? 'bg-white shadow text-gray-900'
                  : 'text-gray-600 hover:text-gray-800'
              }`}  aria-pressed={location.pathname === '/vcheckinout'}>
              Check In / Out
            </button>

            <span className='font-extrabold text-gray-900 px-4 py-2 rounded-md bg-white text-sm shadow'>
              Analytics
            </span>
          </div>
          
          <span className='px-4 py-2'></span>
          
          <div className="flex bg-gray-200 p-1 rounded-lg">
            <button
              type="button"
              onClick={() => setAnalyticsMode('trend')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition ${
                analyticsMode === 'trend'
                  ? 'bg-white shadow text-gray-900'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
             aria-pressed={analyticsMode === 'trend'}>
              Monthly Trend
            </button>
            <button
              type="button"
              onClick={() => setAnalyticsMode('leaderboard')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition ${
                analyticsMode === 'leaderboard'
                  ? 'bg-white shadow text-gray-900'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
             aria-pressed={analyticsMode === 'leaderboard'}>
              Company Leaderboard
            </button>
          </div>          
        </div></>}></PageToolbar></>

          <div className="rounded-xl border bg-white p-4 shadow-sm mb-6">
            <>{/* //codex-- Match this page's toolbar to its asmainframe reference. */}<PageToolbar title="Attendance analytics filters" actions={<>
              {analyticsMode !== 'leaderboard' && <div className="mt-4 text-sm text-gray-500">Current employee:<span className="font-medium text-gray-700">{selectedEmployeeLabel}</span>
                <span className="mx-2">|</span>Year:<span className="font-medium text-gray-700">{selectedYear}</span>
                <span className="mx-2">|</span>Expected start time for lateness:<span className="font-medium text-gray-700">{EXPECTED_START_TIME}</span>
              </div>}
              {analyticsMode === 'leaderboard' && <p className="mt-1 text-xs text-gray-500">Company Leaderboard always includes all technicians.</p>}
            </>}>
              <div>
                <ToolbarField label="Employee"><select className="w-full border border-gray-300 rounded-md px-3 py-2" value={filterEmployee} onChange={e => setFilterEmployee(e.target.value)} disabled={employeesLoading || analyticsMode === 'leaderboard'}>
                    <option value="">Select Employee</option>
                    {employees.map(employee => <option key={employee.id} value={employee.id}>
                      {employee.first_name}
                      {employee.last_name}
                    </option>)}
                  </select></ToolbarField>
              </div>
              <div>
                <ToolbarField label="Year"><select className="w-full border border-gray-300 rounded-md px-3 py-2" value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))}>
                    {yearOptions.map(year => <option key={year} value={year}>
                      {year}
                    </option>)}
                  </select></ToolbarField>
              </div>
              <div>
                <ToolbarField label="Metric"><select className="w-full border border-gray-300 rounded-md px-3 py-2" value={graphMetric} onChange={e => setGraphMetric(e.target.value)}>
                    <option value="avgLateMinutes">Avg Late Minutes</option>
                    <option value="lateCount">Late Count</option>
                    <option value="onTimeRate">On-Time %</option>
                    <option value="totalLateMinutes">Total Late Minutes</option>
                  </select></ToolbarField>
              </div>
              <ToolbarFooterAction><button type="button" onClick={fetchAnalytics} className="w-full px-4 py-2 rounded-md text-white bg-blue-600 hover:bg-blue-700">Refresh</button></ToolbarFooterAction>
            </PageToolbar></>
             {analyticsMode !== 'leaderboard' && (
            <div className="mt-4 text-sm text-gray-500">
              Current employee: <span className="font-medium text-gray-700">{selectedEmployeeLabel}</span>
              <span className="mx-2">|</span>
              Year: <span className="font-medium text-gray-700">{selectedYear}</span>
              <span className="mx-2">|</span>
              Expected start time for lateness: <span className="font-medium text-gray-700">{EXPECTED_START_TIME}</span>
            </div> )}
            {analyticsMode === 'leaderboard' && (
              <p className="mt-1 text-xs text-gray-500">
                Company Leaderboard always includes all technicians.
              </p>
            )}            
          </div>

          {loading ? (
            <LoadingSpinner>Loading analytics...</LoadingSpinner>
          ) : analyticsMode === 'leaderboard' ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="rounded-xl border bg-white p-4 shadow-sm">
                  <p className="text-sm text-gray-500">Employees Measured</p>
                  <p className="mt-1 text-2xl font-bold text-gray-900">
                    {leaderboardSummary.totalEmployees}
                  </p>
                </div>

                <div className="rounded-xl border bg-white p-4 shadow-sm">
                  <p className="text-sm text-gray-500">Most Late Occurrences</p>
                  <p className="mt-1 text-lg font-bold text-gray-900">
                    {leaderboardSummary.worstEmployee?.employee || 'N/A'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {leaderboardSummary.worstEmployee
                      ? `${leaderboardSummary.worstEmployee.lateCount} late check-ins`
                      : ''}
                  </p>
                </div>

                <div className="rounded-xl border bg-white p-4 shadow-sm">
                  <p className="text-sm text-gray-500">Highest Avg Late Minutes</p>
                  <p className="mt-1 text-lg font-bold text-gray-900">
                    {leaderboardSummary.highestAvgLateEmployee?.employee || 'N/A'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {leaderboardSummary.highestAvgLateEmployee
                      ? `${leaderboardSummary.highestAvgLateEmployee.avgLateMinutes} avg mins`
                      : ''}
                  </p>
                </div>
              </div>

              <div className="rounded-xl border bg-white p-4 shadow-sm mb-6">
                <div className="mb-4">
                  <h2 className="text-lg font-semibold text-gray-800">Top 10 Most Late</h2>
                  <p className="text-sm text-gray-500">
                    Ranked by total number of late arrivals for the selected date range.
                  </p>
                </div>

                {leaderboardData.length > 0 ? (
                  renderLeaderboardChart()
                ) : (
                  <div className="py-16 text-center text-gray-500">
                    No leaderboard data available for the selected range.
                  </div>
                )}
              </div>

              <div className="rounded-xl border bg-white p-4 shadow-sm">
                <div className="mb-4">
                  <h2 className="text-lg font-semibold text-gray-800">Company Lateness Ranking</h2>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border px-4 py-2 text-left text-sm font-semibold text-gray-700">Rank</th>
                        <th className="border px-4 py-2 text-left text-sm font-semibold text-gray-700">Employee</th>
                        <th className="border px-4 py-2 text-left text-sm font-semibold text-gray-700">Days</th>
                        <th className="border px-4 py-2 text-left text-sm font-semibold text-gray-700">Late Count</th>
                        <th className="border px-4 py-2 text-left text-sm font-semibold text-gray-700">Late %</th>
                        <th className="border px-4 py-2 text-left text-sm font-semibold text-gray-700">Total Late Minutes</th>
                        <th className="border px-4 py-2 text-left text-sm font-semibold text-gray-700">Avg Late Minutes</th>
                        <th className="border px-4 py-2 text-left text-sm font-semibold text-gray-700">Worst Single Late</th>
                        <th className="border px-4 py-2 text-left text-sm font-semibold text-gray-700">On-Time %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaderboardData.length > 0 ? (
                        leaderboardData.map((row, index) => (
                          <tr key={row.employee}>
                            <td className="border px-4 py-2 text-sm text-gray-700">{index + 1}</td>
                            <td className="border px-4 py-2 text-sm text-gray-700 font-medium">{row.employee}</td>
                            <td className="border px-4 py-2 text-sm text-gray-700">{row.totalDays}</td>
                            <td className="border px-4 py-2 text-sm text-gray-700">{row.lateCount}</td>
                            <td className="border px-4 py-2 text-sm text-gray-700">{row.lateRate}%</td>
                            <td className="border px-4 py-2 text-sm text-gray-700">{row.totalLateMinutes}</td>
                            <td className="border px-4 py-2 text-sm text-gray-700">{row.avgLateMinutes}</td>
                            <td className="border px-4 py-2 text-sm text-gray-700">{row.maxLateMinutes}</td>
                            <td className="border px-4 py-2 text-sm text-gray-700">{row.onTimeRate}%</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="9" className="border px-4 py-8 text-center text-sm text-gray-500">
                            No company lateness data found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
           <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="rounded-xl border bg-white p-4 shadow-sm">
                  <p className="text-sm text-gray-500">Avg Late Minutes</p>
                  <p className="mt-1 text-2xl font-bold text-gray-900">{summary.avgLateMinutes}</p>
                </div>

                <div className="rounded-xl border bg-white p-4 shadow-sm">
                  <p className="text-sm text-gray-500">Late Count</p>
                  <p className="mt-1 text-2xl font-bold text-gray-900">{summary.lateCount}</p>
                </div>

                <div className="rounded-xl border bg-white p-4 shadow-sm">
                  <p className="text-sm text-gray-500">On-Time %</p>
                  <p className="mt-1 text-2xl font-bold text-gray-900">{summary.onTimeRate}%</p>
                </div>

                <div className="rounded-xl border bg-white p-4 shadow-sm">
                  <p className="text-sm text-gray-500">Worst Month</p>
                  <p className="mt-1 text-lg font-bold text-gray-900">
                    {summary.worstMonth ? summary.worstMonth.label : 'N/A'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {summary.worstMonth ? `${summary.worstMonth.avgLateMinutes} avg mins late` : ''}
                  </p>
                </div>
              </div>

              <div className="rounded-xl border bg-white p-4 shadow-sm mb-6">
                <div className="mb-4">
                  <h2 className="text-lg font-semibold text-gray-800">Monthly Lateness Trend</h2>
                  <p className="text-sm text-gray-500">
                    Graph is based on AM check-in records grouped by month.
                  </p>
                </div>

                {graphData.length > 0 ? (
                  renderChart()
                ) : (
                  <div className="py-16 text-center text-gray-500">
                    No graph data available for the selected range.
                  </div>
                )}
              </div>

              <div className="rounded-xl border bg-white p-4 shadow-sm">
                <div className="mb-4">
                  <h2 className="text-lg font-semibold text-gray-800">Monthly Breakdown</h2>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border px-4 py-2 text-left text-sm font-semibold text-gray-700">Month</th>
                        <th className="border px-4 py-2 text-left text-sm font-semibold text-gray-700">Days</th>
                        <th className="border px-4 py-2 text-left text-sm font-semibold text-gray-700">Late Count</th>
                        <th className="border px-4 py-2 text-left text-sm font-semibold text-gray-700">Total Late Minutes</th>
                        <th className="border px-4 py-2 text-left text-sm font-semibold text-gray-700">Avg Late Minutes</th>
                        <th className="border px-4 py-2 text-left text-sm font-semibold text-gray-700">On-Time %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {graphData.length > 0 ? (
                        graphData.map((row) => (
                          <tr key={row.month}>
                            <td className="border px-4 py-2 text-sm text-gray-700">{row.label}</td>
                            <td className="border px-4 py-2 text-sm text-gray-700">{row.totalDays}</td>
                            <td className="border px-4 py-2 text-sm text-gray-700">{row.lateCount}</td>
                            <td className="border px-4 py-2 text-sm text-gray-700">{row.totalLateMinutes}</td>
                            <td className="border px-4 py-2 text-sm text-gray-700">{row.avgLateMinutes}</td>
                            <td className="border px-4 py-2 text-sm text-gray-700">{row.onTimeRate}%</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="border px-4 py-8 text-center text-sm text-gray-500">
                            No monthly data found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttendanceAnalyticsPage;